import { logger } from '@fiverr-private/obs';
import { validateOptions } from '../utils/validateOptions';
import { toParts, FORMATTING_PARTS } from '../utils/toParts';
import { NUMBER_FORMATTING_OPTIONS, NUMBER_STYLES } from './constants';

const NUMBER_PARTS = { ...FORMATTING_PARTS };
delete NUMBER_PARTS.CURRENCY;

/**
 * Format a number.
 * Use this to format numbers, either decimal (i.e '3', '5.3') or percentages (i.e '40%', '98.5%').
 * Return the number as a string, formatted to user formatting locale.
 * Note: if options.forceFormat is passed, we take its value as if we should format or not (and ignore localizationContext.shouldFormat)
 * @param {Object} localizationContext - the localization context that is being injected
 * dynamically according to the run time env by one of the wrappers - withBrowserContext / withNodeContext
 * @param {number} value Number to be formatted.
 * @param {Object} [options={}] Optional formatting configurations.
 * @returns {string|number} Formatted value string.
 */
const numberFormat = (localizationContext, value, options = {}) => {
    try {
        if (!localizationContext || Object.keys(localizationContext).length === 0) {
            return options.fallbackValue;
        }

        const { forceFormat, asNumber, asParts } = options;
        const formattingLocale = options.formattingLocale || localizationContext.formattingLocale;
        if (forceFormat === true || (forceFormat === undefined && localizationContext.shouldFormat)) {
            const parsedOptions = parseOptions(options);
            const formattedString = new Intl.NumberFormat(formattingLocale, parsedOptions).format(value);
            if (asNumber || asParts) {
                const formattedNumber = parseFloat(formattedString.replace(/,/g, '.'));
                return asNumber ? formattedNumber : toParts({
                    localizationContext,
                    formattingLocale,
                    formattedString,
                    formattedNumber
                });
            }
            return formattedString;
        } else {
            return options.fallbackValue || value;
        }
    } catch (e) {
        logger.error(e, { value, options, formatter: 'numberFormat' });
        return options.fallbackValue || value;
    }
};

/**
 * Parse formatter options.
 * This method validates the properties in the options object and overrides properties for Fiverr custom formatting rules.
 * @param {Object} options Optional formatting configurations.
 * @returns {Object} Validated formatting configurations.
 */
const parseOptions = (options) => {
    const validatedOptions = validateOptions(NUMBER_FORMATTING_OPTIONS, options);

    if (options.noFractionDigits) {
        validatedOptions.minimumFractionDigits = 0;
        validatedOptions.maximumFractionDigits = 0;
    }

    if (options.asNumber || options.asParts) {
        validatedOptions.useGrouping = false;
    }

    return validatedOptions;
};

export {
    NUMBER_STYLES,
    NUMBER_PARTS,
    numberFormat,
    parseOptions
};
